/* src/components/pages/ExtraerLibroComprasAnualCli.jsx - Modificado el 19-02-2026 */
/* -------- On Demand LibroCompras ANUAL Cliente -------- */

import React, { useState, useEffect, useCallback, useMemo } from "react";

import {
  Box,
  Stack,
  TextField,
  Button,
  Typography,
  MenuItem,
  LinearProgress,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";

import { RiFileSearchLine } from "react-icons/ri";

import api from "../../services/api.js";

import { useAuth } from "../../context/AuthContext";
import { useFeedback } from "../../context/FeedbackContext";

import { CustomToolbar } from "../../components/datagrid/CustomToolbar";

import { normalizeRut } from "../utils/rutUtils";
import { validarRut } from "../utils/rutValidator";

const ExtraerLibroComprasAnualCli = ({ modo }) => {
  const { user } = useAuth();
  const { showFeedback } = useFeedback();

  /* ===============================
     Estados principales
  =============================== */

  const [bitacora, setBitacora] = useState([]);
  const [loading, setLoading] = useState(false);

  /* Progreso visible */
  const [progresoTexto, setProgresoTexto] = useState("");
  const [progress, setProgress] = useState(null);

  /* Async Job */
  const [jobId, setJobId] = useState(null);

  /* Año */
  const [ano, setAno] = useState(new Date().getFullYear());

  /* Pagination */
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);

  /* ===============================
     Validación Rut Cliente
  =============================== */
  const rutValido = useMemo(() => {
    if (!user?.usuario) return false;
    return validarRut(user.usuario);
  }, [user?.usuario]);

  /* ===============================
     Bitácora server-side
  =============================== */
  const fetchBitacora = useCallback(async () => {
    if (!rutValido) return;

    setLoading(true);

    try {
      const RutCli = normalizeRut(user.usuario);

      const { data } = await api.post("/librocompras/getbitacora", {
        RutCli,
        page,
        pageSize,
      });

      if (data?.success) {
        setBitacora(data.rows || []);
        setRowCount(data.total || 0);
      } else {
        setBitacora([]);
        setRowCount(0);
      }
    } catch {
      showFeedback({
        message: "❌ No fue posible obtener la bitácora",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [rutValido, user?.usuario, page, pageSize, showFeedback]);

  useEffect(() => {
    fetchBitacora();
  }, [fetchBitacora]);

  /* ===============================
     Polling Async Job (ANUAL)
  =============================== */
  useEffect(() => {
    if (!jobId) return;

    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(
          `/extraerlibrocompras/anual/status/${jobId}`
        );

        /* Progreso live */
        if (typeof data.progress === "object") {
          setProgress(data.progress);

          if (data.progress.message) {
            setProgresoTexto(data.progress.message);
          }
        }

        if (data.state === "active" && !progresoTexto) {
          setProgresoTexto("⏳ Iniciando extracción anual...");
        }

        /* Job eliminado */
        if (data.state === "notfound") {
          clearInterval(interval);

          showFeedback({
            message: "⚠️ Job ya no existe (probablemente terminó)",
            severity: "warning",
          });

          await fetchBitacora();

          setJobId(null);
          setProgresoTexto("");
          return;
        }

        /* Completed */
        if (data.state === "completed") {
          clearInterval(interval);

          setProgresoTexto("🎉 Extracción anual completada");

          showFeedback({
            message: "✅ Extracción anual LibroCompras finalizada",
            severity: "success",
          });

          await fetchBitacora();

          setTimeout(() => setProgresoTexto(""), 5000);
          setJobId(null);
          return;
        }

        /* Failed */
        if (data.state === "failed") {
          clearInterval(interval);

          setProgresoTexto("");
          setProgress(null);
          setJobId(null);

          showFeedback({
            message: "❌ Falló extracción anual LibroCompras",
            severity: "error",
          });
        }
      } catch {
        clearInterval(interval);

        setProgresoTexto("");
        setJobId(null);

        showFeedback({
          message: "❌ No fue posible consultar estado del Job",
          severity: "error",
        });
      }
    }, 2500);

    return () => clearInterval(interval);
  }, [jobId, progresoTexto, fetchBitacora, showFeedback]);

  /* ===============================
     Solicitar extracción anual
  =============================== */
  const solicitarExtraccionAnual = async () => {
    if (!rutValido) {
      showFeedback({
        message: "El Rut del cliente no es válido",
        severity: "error",
      });
      return;
    }

    try {
      const RutCli = normalizeRut(user.usuario);

      showFeedback({
        message: `⏳ Iniciando extracción anual Compras ${ano}...`,
        severity: "info",
      });

      const { data } = await api.post("/extraerlibrocompras/anual/start", {
        RutCli,
        TipCli: 1,
        Anoaconsultar: ano,
      });

      if (data?.success) {
        setJobId(data.jobId);

        showFeedback({
          message: "📌 Proceso anual ejecutándose en segundo plano",
          severity: "info",
        });
      } else {
        showFeedback({
          message: data?.message || "No fue posible iniciar extracción anual",
          severity: "error",
        });
      }
    } catch {
      showFeedback({
        message: "❌ Error al iniciar extracción anual",
        severity: "error",
      });
    }
  };

  /* ===============================
     DataGrid Columns
  =============================== */
  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "Id",
        width: 90,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "fecha",
        headerName: "Fecha",
        width: 200,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          if (!params.value) return "—";
          return new Date(params.value).toLocaleString("es-CL");
        },
      },
      {
        field: "tipo",
        headerName: "Tipo",
        width: 100,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "rutcli",
        headerName: "Rut Cliente",
        width: 130,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "rutenr",
        headerName: "Rut Enrolado",
        width: 130,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "nombre",
        headerName: "Razón Social",
        flex: 1,
        minWidth: 280,
        align: "left",
        headerAlign: "center",
      },
      {
        field: "detalle",
        headerName: "Detalle",
        width: 400,
        align: "center",
        headerAlign: "center",
      },
    ],
    []
  );

  /* ===============================
     Render
  =============================== */
  return (
    <Box sx={{ width: "100%", overflow: "visible" }}>
      {/* Barra controles */}
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        alignItems="center"
        sx={{
          mb: 1,
          flexShrink: 0,       // 👈 NO se colapsa
        }}
      >
        <TextField
          label="Rut Cliente"
          value={user?.usuario || ""}
          size="small"
          disabled
        />

        <TextField
          label="Cliente"
          value={user?.nombre || ""}
          size="small"
          disabled
          sx={{ minWidth: 260, flexGrow: 1 }}
        />

        {/* Año */}
        <TextField
          select
          label="Año"
          value={ano}
          onChange={(e) => setAno(e.target.value)}
          size="small"
          sx={{ width: 120 }}
        >
          {Array.from({ length: 7 }, (_, i) => (
            <MenuItem key={i} value={new Date().getFullYear() - i}>
              {new Date().getFullYear() - i}
            </MenuItem>
          ))}
        </TextField>

        {/* Botón */}
        <Button
          variant="contained"
          startIcon={<RiFileSearchLine />}
          onClick={solicitarExtraccionAnual}
          disabled={!!jobId}
        >
          Solicitar Año Completo
        </Button>
      </Stack>

      {/* Progreso */}
      {jobId && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {progresoTexto || "⏳ Ejecutando extracción anual..."}
          </Typography>

          {progress?.current && progress?.total && (
            <LinearProgress
              variant="determinate"
              value={(progress.current / progress.total) * 100}
              sx={{ mt: 0.5 }}
            />
          )}
        </Box>
      )}

      {/* Bitácora */}
      <Box sx={{ height: "calc(100vh - 230px)" }}>
        <DataGrid
          rows={bitacora}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          density="compact"
          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 50]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={({ page, pageSize }) => {
            setPage(page);
            setPageSize(pageSize);
          }}
          slots={{ toolbar: CustomToolbar }}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        />
      </Box>

      <Typography variant="caption" color="text.secondary">
        {rowCount.toLocaleString("es-CL")} registros encontrados
      </Typography>
    </Box>
  );
};

export default ExtraerLibroComprasAnualCli;
